import React from 'react'
import 'views/views.css'
import {
    LineChart as RechartLineChart,
    // CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Line,
    ResponsiveContainer,
} from 'recharts'
import { LoadingIndicator } from 'components'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling';

/**
 * @name LineChart
 * @desc Linehart wrapper
 * @param chartTitle - String that represents the title of the chart
 * @param chartActions - Extended functionality that can exist in the header of the chart container - for example a 'update range' button
 * @param data - Array of data, containing [{name: foo, value: 99},...]
 * @param loading - Bool
 * @param returnCard - Some charts need to be rendered wrapped in a <Grid> component and not a <Card> component. This prop toggles between the two.
 */
const LineChart = ({
    data = [],
    loading = false,
    yAxisPercent = false,
    toolTipValueName = 'Value',
    tickCount = 5,
    disableAutoDomain = false,
    customDomain = undefined,
}: {
    data: { name: string; val: string | number }[]
    loading?: boolean
    yAxisPercent?: boolean
    toolTipValueName?: string
    tickCount?: number
    disableAutoDomain?: boolean // Disable 0 - 100% domain on some percentage charts.
    customDomain?: [number, number] // Custom domain for Y-axis [min, max]
}): React.ReactElement => {
    const { clientTheme } = useThemeStyling()
    
    const CustomTooltipValue = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div
                    className={'emp-lineChart-tooltipContainer'}
                    style={{ fontSize: clientTheme.typography.body1.fontSize }}
                >
                    <table style={{ borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr style={{ backgroundColor: '#b3b3b3' }}>
                                <td
                                    colSpan={2}
                                    className={
                                        'emp-lineChart-tooltipTableHeader '
                                    }
                                >
                                    {label}
                                </td>
                            </tr>
                            <tr>
                                <td
                                    className={'emp-lineChart-tooltipTableRow'}
                                    style={{
                                        borderRight: '1px dotted #b3b3b3',
                                        textAlign: 'center',
                                    }}
                                >
                                    {toolTipValueName}
                                </td>
                                <td
                                    className={'emp-lineChart-tooltipTableRow'}
                                    style={{
                                        textAlign: 'right',
                                    }}
                                >
                                    {payload[0].value}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )
        }

        return null
    }

    const CustomTooltipPercent = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div
                    className={'emp-areaChart-tooltipContainer'}
                    style={{ fontSize: clientTheme.typography.body1.fontSize }}
                >
                    <table style={{ borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr style={{ backgroundColor: '#b3b3b3' }}>
                                <td
                                    className={
                                        'emp-areaChart-tooltipTableHeader'
                                    }
                                >
                                    {label}
                                </td>
                            </tr>

                            <tr>
                                <td
                                    className={'emp-areaChart-tooltipTableRow'}
                                    style={{
                                        textAlign: 'center',
                                    }}
                                >
                                    {`${payload[0].value.toFixed(2)}%`}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )
        }

        return null
    }

    // Format percentage values based on their magnitude
    const formatPercentTick = (val: number): string => {
        if (val === 0) {
            return '0%'
        }

        // For small values (less than 1%), show 2 decimal places
        if (val < 1) {
            return `${val.toFixed(2)}%`
        }

        // For larger values, show 1 decimal place
        return `${val.toFixed(1)}%`
    }

    return (
        <div className={'emp-lineChart-root'}>
            {!loading ? (
                <>
                    {data.length ? (
                        <ResponsiveContainer height={265} width="100%">
                            <RechartLineChart
                                data={data.map((dataPoint) => ({
                                    ...dataPoint,
                                    val: +dataPoint.val,
                                }))}
                                margin={{
                                    top: 20,
                                    left: -10,
                                    bottom: 5,
                                }}
                            >
                                {/* <CartesianGrid strokeDasharray="3 3" /> */}
                                <XAxis
                                    dataKey="name"
                                    angle={90}
                                    textAnchor="start"
                                    height={70}
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                {yAxisPercent ? (
                                    <YAxis
                                        {...(disableAutoDomain
                                            ? { interval: 0 }
                                            : {})}
                                        tickCount={tickCount}
                                        domain={
                                            customDomain || (yAxisPercent ? [0, 100] : undefined)
                                        }
                                        tickFormatter={formatPercentTick}
                                        tick={{
                                            fontSize:
                                                clientTheme.typography.label
                                                    .fontSize,
                                        }}
                                    />
                                ) : (
                                    <YAxis
                                        tick={{
                                            fontSize:
                                                clientTheme.typography.label
                                                    .fontSize,
                                        }}
                                    />
                                )}
                                {yAxisPercent ? (
                                    <Tooltip
                                        content={<CustomTooltipPercent />}
                                    />
                                ) : (
                                    <Tooltip content={<CustomTooltipValue />} />
                                )}
                                <Line
                                    type="monotone"
                                    dataKey="val"
                                    stroke={
                                        clientTheme.charts.lineCharts.stroke
                                    }
                                    fill={clientTheme.charts.lineCharts.fill}
                                />
                            </RechartLineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className={'emp-lineChart-noDataMessaging'}>
                            No Data
                        </div>
                    )}
                </>
            ) : (
                <div className={'emp-lineChart-loadingContainer'}>
                    <LoadingIndicator />
                </div>
            )}
        </div>
    )
}

export default LineChart
